import React, { useCallback, useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useToolStore, useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import { BORDER_RADIUS, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import useToolIntentSuggestions from '@/hooks/useToolIntentSuggestions';
import useWorkModeIntentSuggestions from '@/hooks/useWorkModeIntentSuggestions';
import { isDesktopEditorSidebarType } from '@/constants/utils/files';

const createStyles = (colors, isXS, messageWidth, variant) => StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: variant === 'conversation' ? (isXS ? 15 : 20) : 12,
    paddingVertical: 6,
    marginBottom: variant === 'conversation' ? 8 : 0,
    maxWidth: variant === 'conversation'
      ? (messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%')
      : '100%',
    marginLeft: variant === 'conversation' ? 'auto' : undefined,
    marginRight: variant === 'conversation' ? 'auto' : undefined,
    flexShrink: 0,
    position: 'relative',
    zIndex: variant === 'conversation' ? 12 : 1,
    ...(Platform.OS === 'web' && variant === 'conversation' ? { boxSizing: 'border-box' } : {}),
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: variant === 'conversation' ? colors.solid_color_block : colors.solid_color_tertiary,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      },
    }),
  },
  chipHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  chipLabel: {
    flexShrink: 1,
  },
});

const ToolIntentSuggestions = ({ contextId, variant = 'home' }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { t } = useTranslation();
  const messageWidth = useUIStore(state => state.messageWidth);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth, variant),
    [colors, isXS, messageWidth, variant]
  );

  const fetchCatalog = useToolStore(state => state.fetchCatalog);
  const isFetchedCatalog = useToolStore(state => state.isFetchedCatalog);
  const isLoadingCatalog = useToolStore(state => state.isLoadingCatalog);
  const { suggestions } = useToolIntentSuggestions(contextId);
  const { shouldSuggest: prefersWorkMode } = useWorkModeIntentSuggestions(contextId);

  // Prefetch on mount so matches are ready by the time the user types.
  useEffect(() => {
    if (!isFetchedCatalog && !isLoadingCatalog) {
      fetchCatalog().catch(() => {});
    }
  }, [isFetchedCatalog, isLoadingCatalog, fetchCatalog]);

  const handlePress = useCallback((tool) => {
    trackEvent('tools', 'intent_suggestion_clicked', tool.slug);
    router.push(`/tools/${tool.slug}`);
  }, [router]);

  // Keep chat clear of tool upsells while a document / sheet / deck editor is open.
  if (isContextSidebarVisible && isDesktopEditorSidebarType(contextSidebarType)) return null;
  // A single-shot tool can't finish an agentic prompt, so Work replaces the
  // tool chip whenever both match rather than stacking two upsells.
  if (prefersWorkMode) return null;
  if (!suggestions.length) return null;

  return (
    <View style={styles.container} accessibilityRole="list">
      {suggestions.map(tool => (
        <Pressable
          key={tool.slug}
          style={({ hovered, pressed }) => [
            styles.chip,
            hovered && styles.chipHovered,
            pressed && { opacity: 0.8 },
          ]}
          onPress={() => handlePress(tool)}
          accessibilityRole="button"
          accessibilityLabel={t('home.tryTool', { name: tool.name })}
        >
          <Icon name="wand-magic-sparkles" size={12} color={colors.text_color_secondary} />
          <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
            {t('home.tryTool', { name: tool.name })}
          </Text>
          <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
        </Pressable>
      ))}
    </View>
  );
};

export default React.memo(ToolIntentSuggestions);
